import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { ownResourceKey } from '../../../ownKey'
import type { CashierProblem, PosCashierChoice, PosCashierGrant } from './cashier'
import { cashierReducer, initialCashier, type CashierAction, type CashierState } from './cashierState'

/**
 * Кассир окна «РМК» по источникам (намерение: кого выбрали, подтверждён ли кодом, идёт ли проверка). Срок жизни `screen`: только
 * в памяти вкладки — не в хранилище браузера, не в адресе; закрытие окна и смена пользователя подтверждение забывают.
 * Код и токен подтверждения сюда не попадают (см. `cashierState.ts`). Правила переходов — `cashierReducer`.
 */
export interface OwnCashierSliceState {
  byResource: Record<string, CashierState>
}

const initialState: OwnCashierSliceState = { byResource: {} }

type Payload<T = unknown> = PayloadAction<{ resourceId: string } & T>

function apply(state: OwnCashierSliceState, resourceId: string, action: CashierAction): void {
  const key = ownResourceKey(resourceId)
  const stored = state.byResource[key]
  const before = stored ?? initialCashier
  const after = cashierReducer(before, action)
  if (after !== before) state.byResource[key] = after
}

const slice = createSlice({
  name: 'own/cashier',
  initialState,
  reducers: {
    ownCashierReset(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'reset' })
    },
    ownCashierListReady(state, { payload }: Payload<{ items: readonly PosCashierChoice[] }>) {
      apply(state, payload.resourceId, { type: 'list-ready', items: payload.items })
    },
    ownCashierListFailed(state, { payload }: Payload<{ problem: CashierProblem }>) {
      apply(state, payload.resourceId, { type: 'list-error', problem: payload.problem })
    },
    ownCashierSelected(state, { payload }: Payload<{ id: string | null }>) {
      apply(state, payload.resourceId, { type: 'selected', id: payload.id })
    },
    ownCashierVerifyStarted(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'verify-started' })
    },
    ownCashierVerified(state, { payload }: Payload<{ grant: PosCashierGrant }>) {
      apply(state, payload.resourceId, { type: 'verify-ok', grant: payload.grant })
    },
    ownCashierVerifyFailed(state, { payload }: Payload<{ problem: CashierProblem }>) {
      apply(state, payload.resourceId, { type: 'verify-failed', problem: payload.problem })
    },
    ownCashierExpired(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'expired' })
    },
    ownCashierForgotten(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'forgotten' })
    },
    ownCashierWindowClosed(state, { payload }: Payload) {
      delete state.byResource[ownResourceKey(payload.resourceId)]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[ownResourceKey(action.payload.resourceId)]
    })
  },
})

export const {
  ownCashierReset,
  ownCashierListReady,
  ownCashierListFailed,
  ownCashierSelected,
  ownCashierVerifyStarted,
  ownCashierVerified,
  ownCashierVerifyFailed,
  ownCashierExpired,
  ownCashierForgotten,
  ownCashierWindowClosed,
} = slice.actions
export const ownCashierSliceReducer = slice.reducer

/** Кассир источника; нет записи — список ещё не читали. */
export const cashierOf = (state: OwnCashierSliceState, resourceId: string): CashierState => state.byResource[ownResourceKey(resourceId)] ?? initialCashier
